import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Activity, CheckCircle2, CloudRain, Crosshair, MapPin, Radar, ShieldCheck, Star, TrendingUp } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const intelligenceCards = [
  { icon: MapPin, title: "Pinned microclimates", text: "Track rain and snow totals for the exact fields, docks, stands, and home locations that matter." },
  { icon: ShieldCheck, title: "Forecast trust scoring", text: "Cross-check live sources and alert context before you trust a forecast window." },
  { icon: Radar, title: "High-contrast radar", text: "RainViewer radar, SPC outlooks, and a clearer snow/rain contrast built for decision-making." },
  { icon: Crosshair, title: "Outdoor intelligence", text: "Hunting, lake, marine, ski, land, health, and severe-weather tools in one location-aware dashboard." },
];

const comparisonRows = [
  ["Hyperlocal saved locations", "City-level weather", "Pin-specific rain/snow intelligence"],
  ["Radar clarity", "Generic overlays", "High-contrast rain/snow + SPC outlooks"],
  ["Decision tools", "Hourly forecast", "Land, lake, hunting, ski, marine, and trust scoring"],
  ["Premium purpose", "Ads and broad forecasts", "Actionable Location Intelligence"],
];

const heroProofItems = [
  { id: "rain-snow-pins", label: "Rain/Snow pins", detail: "Exact-place totals", icon: MapPin },
  { id: "forecast-trust-score", label: "Forecast Trust Score", detail: "Source confidence", icon: ShieldCheck },
  { id: "lake-marine-alerts", label: "Lake + marine alerts", detail: "Outdoor timing", icon: Activity },
  { id: "storm-chaser-streams", label: "Storm chaser streams", detail: "Live severe context", icon: Radar },
];

const isRealApprovedReview = (review) => {
  const searchableText = [review.id, review.display_name, review.title, review.content]
    .filter(Boolean)
    .join(" ");

  return !/(seed-\d+|test_iter\d+|\biter\s*\d+\s*ui\b|\bui pending check\b)/i.test(searchableText);
};

export default function LandingPage() {
  const [reviews, setReviews] = useState([]);

  useEffect(() => {
    axios.get(`${API}/reviews`)
      .then((response) => {
        setReviews((response.data.reviews || []).slice(0, 3));
      })
      .catch(() => {
        setReviews([]);
      });
  }, []);

  const visibleReviews = reviews.filter(isRealApprovedReview);
  const visibleReviewStats = visibleReviews.length
    ? {
        average: (visibleReviews.reduce((sum, review) => sum + (Number(review.rating) || 0), 0) / visibleReviews.length).toFixed(1),
        total: visibleReviews.length,
      }
    : { average: 0, total: 0 };

  return (
    <main className="landing-dark min-h-screen bg-[#070A10] text-white selection:bg-cyan-300 selection:text-slate-950" data-testid="public-landing-page">
      <nav className="sticky top-0 z-50 border-b border-white/10 bg-[#070A10]/90 backdrop-blur-xl" data-testid="landing-nav">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4">
          <Link to="/" className="flex items-center gap-2 font-black tracking-tight text-white" data-testid="landing-logo-link">
            <span className="flex h-9 w-9 items-center justify-center rounded-xl border border-cyan-300/30 bg-cyan-300 text-slate-950" data-testid="landing-logo-icon">
              <CloudRain className="h-5 w-5" />
            </span>
            TheForecastTracker
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/login" className="hidden text-sm font-semibold text-slate-300 transition-colors hover:text-white sm:inline" data-testid="landing-login-link">Log in</Link>
            <Link to="/register" className="rounded-full bg-cyan-300 px-4 py-2 text-sm font-black text-slate-950 transition-[background-color,transform] hover:-translate-y-0.5 hover:bg-cyan-200" data-testid="landing-register-button">Start tracking</Link>
          </div>
        </div>
      </nav>

      <section className="relative isolate overflow-hidden border-b border-white/10 bg-[#070A10]" data-testid="landing-hero-section">
        <div className="absolute inset-0 -z-10 bg-[linear-gradient(rgba(255,255,255,0.075)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.075)_1px,transparent_1px)] [background-size:44px_44px]" />
        <div className="absolute inset-x-0 bottom-0 -z-10 h-1/2 bg-gradient-to-t from-cyan-950/30 via-[#070A10] to-transparent" />
        <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-5 py-14 lg:grid-cols-12 lg:gap-16 lg:py-10">
          <div className="lg:col-span-6" data-testid="landing-hero-copy-column">
            <p className="mb-6 inline-flex items-center gap-2 rounded-full border border-cyan-300/35 bg-cyan-300/10 px-4 py-2 text-xs font-bold uppercase tracking-[0.2em] text-cyan-200" data-testid="landing-hero-badge">
              <span className="h-2 w-2 rounded-full bg-cyan-300" />
              Premium Location Intelligence
            </p>
            <h1 className="max-w-4xl text-5xl font-black leading-none tracking-tight text-white sm:text-6xl" data-testid="landing-hero-headline">
              Weather apps tell you the city. We track the exact places you depend on.
            </h1>
            <p className="mt-7 max-w-2xl text-lg font-medium leading-relaxed text-slate-300 sm:text-xl" data-testid="landing-hero-subheadline">
              TheForecastTracker turns radar, rain pins, source confidence, and outdoor forecasts into decision-grade intelligence for land, lake, farm, hunting, storm, and travel planning.
            </p>
            <div className="mt-9 flex flex-col gap-3 sm:flex-row" data-testid="landing-hero-cta-group">
              <Link to="/register" className="inline-flex items-center justify-center rounded-full bg-cyan-300 px-7 py-4 font-black text-slate-950 transition-[background-color,transform] hover:-translate-y-0.5 hover:bg-cyan-200" data-testid="landing-hero-register-button">Build my tracker</Link>
              <Link to="/subscription" className="inline-flex items-center justify-center rounded-full border border-white/20 bg-white/5 px-7 py-4 font-black text-white transition-[background-color,transform] hover:-translate-y-0.5 hover:bg-white/10" data-testid="landing-hero-pricing-link">See premium</Link>
            </div>
            <div className="mt-8 hidden max-w-xl grid-cols-3 gap-3 text-sm sm:grid" data-testid="landing-hero-stats-row">
              <div className="border-l-2 border-cyan-300 pl-3" data-testid="landing-hero-stat-saved-places">
                <p className="font-black text-white">Saved places</p>
                <p className="text-slate-400">Fields, lakes, stands</p>
              </div>
              <div className="border-l-2 border-lime-300 pl-3" data-testid="landing-hero-stat-source-checks">
                <p className="font-black text-white">Source checks</p>
                <p className="text-slate-400">Trust before action</p>
              </div>
              <div className="border-l-2 border-amber-300 pl-3" data-testid="landing-hero-stat-live-radar">
                <p className="font-black text-white">Live radar</p>
                <p className="text-slate-400">Rain + storm context</p>
              </div>
            </div>
          </div>

          <div className="lg:col-span-6" data-testid="landing-hero-visual-column">
            <div className="relative mx-auto max-w-xl rounded-[2rem] border border-white/10 bg-white/8 p-4 shadow-2xl shadow-cyan-950/50 backdrop-blur-xl" data-testid="landing-hero-proof-card">
              <div className="overflow-hidden rounded-[1.5rem] border border-white/10 bg-[#050816] text-white" data-testid="landing-hero-dashboard-preview">
                <div className="flex items-center justify-between border-b border-white/10 px-5 py-4">
                  <div>
                    <p className="text-xs font-bold uppercase tracking-[0.2em] text-cyan-300" data-testid="landing-hero-preview-label">Live decision board</p>
                    <p className="mt-1 text-lg font-black" data-testid="landing-hero-preview-location">Decherd farm + lake route</p>
                  </div>
                  <span className="rounded-full bg-lime-300 px-3 py-1 text-xs font-black text-slate-950" data-testid="landing-hero-preview-status">92% Trust</span>
                </div>
                <div className="grid gap-px bg-white/10 sm:grid-cols-2" data-testid="landing-hero-proof-grid">
                  {heroProofItems.map(({ id, label, detail, icon: Icon }) => (
                    <article key={id} className="bg-[#050816] p-5" data-testid={`landing-hero-proof-item-${id}`}>
                      <div className="mb-5 flex items-center justify-between">
                        <Icon className="h-6 w-6 text-cyan-300" />
                        <CheckCircle2 className="h-5 w-5 text-lime-300" />
                      </div>
                      <h2 className="text-lg font-black text-white" data-testid={`landing-hero-proof-title-${id}`}>{label}</h2>
                      <p className="mt-1 text-sm text-slate-300" data-testid={`landing-hero-proof-detail-${id}`}>{detail}</p>
                    </article>
                  ))}
                </div>
                <div className="p-5" data-testid="landing-hero-radar-strip">
                  <div className="mb-3 flex items-center justify-between text-sm">
                    <span className="font-bold text-slate-200">Next action window</span>
                    <span className="text-cyan-300">2:00–5:00 PM</span>
                  </div>
                  <div className="h-3 overflow-hidden rounded-full bg-white/10">
                    <div className="h-full w-3/4 rounded-full bg-gradient-to-r from-cyan-300 via-lime-300 to-amber-300" data-testid="landing-hero-window-bar" />
                  </div>
                </div>
              </div>
              <div className="absolute -bottom-5 -left-4 hidden rounded-2xl border border-white/10 bg-[#111827] px-5 py-4 shadow-xl shadow-cyan-950/50 sm:block" data-testid="landing-hero-floating-note">
                <div className="flex items-center gap-3">
                  <TrendingUp className="h-5 w-5 text-cyan-300" />
                  <div>
                    <p className="text-sm font-black text-white">Location-first forecast</p>
                    <p className="text-xs text-slate-400">Built around places you save</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 py-20" data-testid="landing-location-intelligence-section">
        <div className="mb-10 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.22em] text-cyan-300">Why premium is different</p>
            <h2 className="mt-3 text-3xl font-black tracking-tight text-white sm:text-5xl">Location Intelligence, not weather noise.</h2>
          </div>
          <p className="max-w-xl text-slate-400">Free weather apps are built for broad awareness. TheForecastTracker is built for precise repeat decisions around places you save.</p>
        </div>
        <div className="grid gap-px overflow-hidden border border-white/10 bg-white/10 md:grid-cols-4" data-testid="landing-intelligence-grid">
          {intelligenceCards.map(({ icon: Icon, title, text }) => (
            <article key={title} className="bg-[#111827] p-6 transition-[transform,background-color] hover:-translate-y-1 hover:bg-[#132235]" data-testid={`landing-intelligence-card-${title.toLowerCase().replaceAll(' ', '-')}`}>
              <Icon className="mb-8 h-7 w-7 text-cyan-300" />
              <h3 className="mb-3 text-xl font-black text-white">{title}</h3>
              <p className="text-sm leading-relaxed text-slate-400">{text}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="border-y border-white/10 bg-[#0B0F17]" data-testid="landing-comparison-section">
        <div className="mx-auto grid max-w-7xl gap-10 px-5 py-20 lg:grid-cols-[0.8fr_1.2fr]">
          <div className="aspect-[4/3] border border-white/10 bg-[#050816] p-5" data-testid="landing-data-visual">
            <div className="h-full w-full border border-cyan-300/30 bg-[linear-gradient(135deg,rgba(34,211,238,0.20),transparent_35%),radial-gradient(circle_at_70%_35%,rgba(250,204,21,0.24),transparent_18%),radial-gradient(circle_at_35%_70%,rgba(74,222,128,0.20),transparent_22%)] p-4">
              <div className="grid h-full grid-cols-5 gap-2">
                {Array.from({ length: 25 }).map((_, index) => (
                  <div key={index} className={`border border-white/10 ${index % 7 === 0 ? "bg-cyan-300/45" : index % 5 === 0 ? "bg-lime-300/35" : "bg-white/10"}`} />
                ))}
              </div>
            </div>
          </div>
          <div>
            <h2 className="text-3xl font-black tracking-tight text-white sm:text-5xl">Better than free weather because it remembers your places.</h2>
            <div className="mt-8 overflow-hidden border border-white/10" data-testid="landing-comparison-table">
              {comparisonRows.map(([feature, free, forecast]) => (
                <div key={feature} className="grid border-b border-white/10 last:border-b-0 md:grid-cols-3">
                  <div className="bg-white p-4 font-bold text-slate-950">{feature}</div>
                  <div className="bg-[#0B0F17] p-4 text-slate-500">{free}</div>
                  <div className="bg-cyan-300/10 p-4 font-semibold text-cyan-200">{forecast}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {visibleReviews.length > 0 && (
        <section className="mx-auto max-w-7xl px-5 py-20" data-testid="landing-testimonials-section">
          <div className="mb-10 flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.22em] text-cyan-300">Moderated reviews</p>
              <h2 className="text-3xl font-black tracking-tight text-white sm:text-5xl">What users are saying</h2>
            </div>
            <div className="flex items-center gap-2 text-amber-300" data-testid="landing-review-stats">
              <Star className="h-5 w-5 fill-amber-300" />
              <span className="font-black text-white">{visibleReviewStats.average}</span>
              <span className="text-sm text-slate-400">from {visibleReviewStats.total} approved reviews</span>
            </div>
          </div>
          <div className="grid gap-4 md:grid-cols-3">
            {visibleReviews.map((review) => (
              <article key={review.id} className="border border-white/10 bg-[#111827] p-6 shadow-sm" data-testid={`landing-testimonial-${review.id}`}>
                <div className="mb-5 flex gap-1 text-amber-300">
                  {Array.from({ length: review.rating || 5 }).map((_, index) => <Star key={index} className="h-4 w-4 fill-amber-300" />)}
                </div>
                <h3 className="mb-2 font-black text-white">{review.title}</h3>
                <p className="text-sm leading-relaxed text-slate-400">“{review.content}”</p>
                <div className="mt-5 flex items-center justify-between text-sm">
                  <span className="font-bold text-slate-200">{review.display_name}</span>
                  {review.verified_purchase && <span className="text-xs font-bold uppercase tracking-widest text-cyan-300">Verified</span>}
                </div>
              </article>
            ))}
          </div>
          <div className="mt-10 flex flex-wrap gap-3">
            <Link to="/register" className="rounded-full bg-cyan-300 px-6 py-3 font-black text-slate-950 transition-[background-color,transform] hover:-translate-y-0.5 hover:bg-cyan-200" data-testid="landing-testimonials-register-button">Create account</Link>
            <Link to="/login" className="rounded-full border border-white/20 bg-white/5 px-6 py-3 font-bold text-white transition-[background-color,transform] hover:-translate-y-0.5 hover:bg-white/10" data-testid="landing-testimonials-login-link">Submit a review</Link>
          </div>
        </section>
      )}

      <section className="border-t border-white/10 bg-[#050816] px-5 py-16 text-center text-white" data-testid="landing-final-cta-section">
        <Activity className="mx-auto mb-5 h-8 w-8 text-cyan-300" />
        <h2 className="mx-auto max-w-3xl text-3xl font-black tracking-tight sm:text-5xl">Turn weather into a location advantage.</h2>
        <p className="mx-auto mt-4 max-w-xl text-slate-300">Start with saved locations, then unlock the premium intelligence layer built around your land, water, and outdoor decisions.</p>
        <Link to="/register" className="mt-8 inline-flex rounded-full bg-white px-7 py-3 font-black text-slate-950 transition-[transform,background-color] hover:-translate-y-0.5 hover:bg-cyan-100" data-testid="landing-final-register-button">Get started</Link>
      </section>
    </main>
  );
}